// src/components/InstructionRankingPanel.jsx
// 診療科別 / 術式別 / 指導者別 の指導回数ランキング表示。
// staff_assignments (pairing_mode='instructor') を集計。
// 期間: 1ヶ月/3ヶ月/半年/1年/全期間 から選択可能。

import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';

const PERIOD_OPTIONS = [
  { value: '1m', label: '過去1ヶ月', days: 30 },
  { value: '3m', label: '過去3ヶ月', days: 90 },
  { value: '6m', label: '過去半年', days: 180 },
  { value: '1y', label: '過去1年', days: 365 },
  { value: 'all', label: '全期間', days: null },
];

const InstructionRankingPanel = ({
  // 指定があれば該当 procedure_id だけにフィルタ (人員配置モーダルから呼ぶ用)
  filterProcedureId = null,
  defaultPeriod = '3m',
  compact = false,
}) => {
  const { currentOrganization } = useOrganization();
  const [period, setPeriod] = useState(defaultPeriod);
  const [groupBy, setGroupBy] = useState('department'); // department | procedure | instructor
  const [filterDeptId, setFilterDeptId] = useState('all');     // 🔥 診療科で絞り込み
  const [filterProcId, setFilterProcId] = useState('all');     // 🔥 術式で絞り込み
  const [assignments, setAssignments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [staffMap, setStaffMap] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    (async () => {
      setLoading(true);
      try {
        const opt = PERIOD_OPTIONS.find((p) => p.value === period) || PERIOD_OPTIONS[1];
        const since = opt.days
          ? dayjs().subtract(opt.days, 'day').format('YYYY-MM-DD')
          : '1900-01-01';

        let q = supabase
          .from('staff_assignments')
          .select('id, staff_id, employee_id, procedure_id, assignment_date, pairing_mode')
          .eq('organization_id', currentOrganization.id)
          .eq('pairing_mode', 'instructor')
          .gte('assignment_date', since)
          .limit(10000);
        if (filterProcedureId) q = q.eq('procedure_id', filterProcedureId);

        const [asgRes, prcRes, deptRes, stfRes] = await Promise.all([
          q,
          supabase.from('procedures')
            .select('id, name, department_id, level_id')
            .eq('organization_id', currentOrganization.id),
          supabase.from('departments')
            .select('id, name')
            .eq('organization_id', currentOrganization.id),
          supabase.from('staff')
            .select('id, employee_id, last_name, first_name')
            .eq('organization_id', currentOrganization.id),
        ]);
        if (asgRes.error) throw asgRes.error;
        setAssignments(asgRes.data || []);
        setProcedures(prcRes.data || []);
        setDepartments(deptRes.data || []);
        const sMap = {};
        (stfRes.data || []).forEach((s) => {
          sMap[s.id] = `${s.last_name || ''} ${s.first_name || ''}`.trim() || s.employee_id;
        });
        setStaffMap(sMap);
      } catch (e) {
        console.error('指導ランキング取得失敗:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentOrganization?.id, period, filterProcedureId]);

  const ranking = useMemo(() => {
    const procMap = Object.fromEntries(procedures.map((p) => [p.id, p]));
    const deptMap = Object.fromEntries(departments.map((d) => [d.id, d]));
    const counts = new Map();
    assignments.forEach((a) => {
      const proc = procMap[a.procedure_id];
      // 🔥 診療科/術式フィルタを適用
      if (filterDeptId !== 'all' && proc?.department_id !== filterDeptId) return;
      if (filterProcId !== 'all' && a.procedure_id !== filterProcId) return;

      let key, label, sub;
      if (groupBy === 'department') {
        const deptId = proc?.department_id;
        const dept = deptMap[deptId];
        key = deptId || '__unknown__';
        label = dept?.name || '(不明)';
      } else if (groupBy === 'procedure') {
        key = a.procedure_id || '__unknown__';
        label = proc?.name || '(不明)';
        sub = deptMap[proc?.department_id]?.name;
      } else {
        // instructor
        key = a.staff_id || '__unknown__';
        label = staffMap[a.staff_id] || a.employee_id || '(不明)';
      }
      if (!counts.has(key)) counts.set(key, { key, label, sub, count: 0 });
      counts.get(key).count += 1;
    });
    return [...counts.values()].sort((a, b) => b.count - a.count);
  }, [assignments, procedures, departments, staffMap, groupBy, filterDeptId, filterProcId]);

  // 🔥 診療科 or 術式で絞り込んだら自動的に「指導者別」表示に切替
  useEffect(() => {
    if (filterDeptId !== 'all' || filterProcId !== 'all') {
      setGroupBy('instructor');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDeptId, filterProcId]);

  const totalCount = ranking.reduce((s, r) => s + r.count, 0);
  const topCount = ranking[0]?.count || 1;

  return (
    <div style={{
      background: 'white', borderRadius: '12px',
      padding: compact ? '14px' : '20px',
      border: '1px solid #e5e7eb',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: compact ? 14 : 16, fontWeight: 700, color: '#0f172a' }}>
          📊 指導回数ランキング {filterProcedureId && <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>(この術式のみ)</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, flexWrap: 'wrap' }}>
          {!filterProcedureId && (
            <>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={{
                padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12,
              }}>
                <option value="department">診療科別</option>
                <option value="procedure">術式別</option>
                <option value="instructor">指導者別</option>
              </select>
              {/* 🔥 診療科フィルタ: 選ぶと自動で「指導者別」表示に */}
              <select value={filterDeptId} onChange={(e) => setFilterDeptId(e.target.value)} style={{
                padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12,
              }}>
                <option value="all">全診療科</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {/* 🔥 術式フィルタ: 選ぶと自動で「指導者別」表示に */}
              <select value={filterProcId} onChange={(e) => setFilterProcId(e.target.value)} style={{
                padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12,
              }}>
                <option value="all">全術式</option>
                {procedures.filter((p) => filterDeptId === 'all' || p.department_id === filterDeptId)
                  .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </>
          )}
          {filterProcedureId && (
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={{
              padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12,
            }}>
              <option value="instructor">指導者別</option>
            </select>
          )}
          <select value={period} onChange={(e) => setPeriod(e.target.value)} style={{
            padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12,
          }}>
            {PERIOD_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {loading && <div style={{ color: '#94a3b8', padding: 20, textAlign: 'center' }}>読み込み中...</div>}
      {!loading && ranking.length === 0 && (
        <div style={{ color: '#94a3b8', padding: 20, textAlign: 'center', fontSize: 13 }}>
          該当期間に指導記録がありません
        </div>
      )}

      {!loading && ranking.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
            合計 {totalCount} 回 / {ranking.length} {groupBy === 'department' ? '診療科' : groupBy === 'procedure' ? '術式' : '指導者'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ranking.slice(0, compact ? 5 : 20).map((r, idx) => (
              <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <div style={{
                  width: 24, textAlign: 'center', fontWeight: 700,
                  color: idx === 0 ? '#dc2626' : idx === 1 ? '#ea580c' : idx === 2 ? '#ca8a04' : '#64748b',
                }}>{idx + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.label}
                  </div>
                  {r.sub && <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.sub}</div>}
                </div>
                <div style={{ flex: 2, position: 'relative', height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    position: 'absolute', left: 0, top: 0, height: '100%',
                    width: `${Math.round((r.count / topCount) * 100)}%`,
                    background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
                  }} />
                </div>
                <div style={{ width: 50, textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                  {r.count} 回
                </div>
              </div>
            ))}
            {ranking.length > (compact ? 5 : 20) && (
              <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 4 }}>
                他 {ranking.length - (compact ? 5 : 20)} 件
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InstructionRankingPanel;
